/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateWorkflowSchedulePathParameters,
  DeleteWorkflowSchedulePathParameters,
  GetWorkflowSchedulesParams,
  GetWorkflowSchedulesPathParameters,
  TLHTTPValidationError,
  TLWorkflowScheduleCreateRequest,
  TLWorkflowScheduleResponse,
  TLWorkflowSchedulesResponse,
  TLWorkflowScheduleUpdateRequest,
  UpdateWorkflowSchedulePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Workflow Schedules
 */
export const getWorkflowSchedules = (
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowSchedulesResponse>(
    { url: `/api/v2/workflows/${workflowId}/schedules`, method: 'GET', params, signal },
    options,
  );
};

export const getGetWorkflowSchedulesQueryKey = (
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
) => {
  return ['api', 'v2', 'workflows', workflowId, 'schedules', ...(params ? [params] : [])] as const;
};

export const getGetWorkflowSchedulesQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowSchedulesQueryKey({ workflowId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowSchedules>>> = ({ signal }) =>
    getWorkflowSchedules({ workflowId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetWorkflowSchedulesQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowSchedules>>>;
export type GetWorkflowSchedulesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowSchedules<
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowSchedulesPathParameters,
  params: undefined | GetWorkflowSchedulesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowSchedules>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowSchedules<
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowSchedules>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowSchedules<
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Schedules
 */

export function useGetWorkflowSchedules<
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowSchedulesQueryOptions({ workflowId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Schedules
 */
export const prefetchGetWorkflowSchedulesQuery = async <
  TData = Awaited<ReturnType<typeof getWorkflowSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowSchedulesQueryOptions({ workflowId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Schedules
 */
export const invalidateGetWorkflowSchedules = async (
  queryClient: QueryClient,
  { workflowId }: GetWorkflowSchedulesPathParameters,
  params?: GetWorkflowSchedulesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowSchedulesQueryKey({ workflowId }, params) }, options);

  return queryClient;
};

/**
 * @summary Create Workflow Schedule
 */
export const createWorkflowSchedule = (
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowScheduleResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/schedules`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLWorkflowScheduleCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateWorkflowScheduleQueryKey = (
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest?: BodyType<TLWorkflowScheduleCreateRequest>,
) => {
  return ['POST', 'api', 'v2', 'workflows', workflowId, 'schedules', tLWorkflowScheduleCreateRequest] as const;
};

export const getCreateWorkflowScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateWorkflowScheduleQueryKey({ workflowId }, tLWorkflowScheduleCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createWorkflowSchedule>>> = ({ signal }) =>
    createWorkflowSchedule({ workflowId }, tLWorkflowScheduleCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateWorkflowScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof createWorkflowSchedule>>>;
export type CreateWorkflowScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof createWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof createWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Workflow Schedule
 */

export function useCreateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateWorkflowScheduleQueryOptions({ workflowId }, tLWorkflowScheduleCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Workflow Schedule
 */
export const prefetchCreateWorkflowScheduleQuery = async <
  TData = Awaited<ReturnType<typeof createWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateWorkflowScheduleQueryOptions({ workflowId }, tLWorkflowScheduleCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Workflow Schedule
 */
export const invalidateCreateWorkflowSchedule = async (
  queryClient: QueryClient,
  { workflowId }: CreateWorkflowSchedulePathParameters,
  tLWorkflowScheduleCreateRequest: BodyType<TLWorkflowScheduleCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateWorkflowScheduleQueryKey({ workflowId }, tLWorkflowScheduleCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Workflow Schedule
 */
export const deleteWorkflowSchedule = (
  { workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/api/v2/workflows/${workflowId}/schedules/${scheduleId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteWorkflowScheduleQueryKey = ({ workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters) => {
  return ['DELETE', 'api', 'v2', 'workflows', workflowId, 'schedules', scheduleId] as const;
};

export const getDeleteWorkflowScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteWorkflowScheduleQueryKey({ workflowId, scheduleId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteWorkflowSchedule>>> = ({ signal }) =>
    deleteWorkflowSchedule({ workflowId, scheduleId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteWorkflowScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof deleteWorkflowSchedule>>>;
export type DeleteWorkflowScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteWorkflowSchedule<
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteWorkflowSchedulePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof deleteWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteWorkflowSchedule<
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteWorkflowSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof deleteWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteWorkflowSchedule<
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteWorkflowSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Workflow Schedule
 */

export function useDeleteWorkflowSchedule<
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteWorkflowScheduleQueryOptions({ workflowId, scheduleId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Workflow Schedule
 */
export const prefetchDeleteWorkflowScheduleQuery = async <
  TData = Awaited<ReturnType<typeof deleteWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteWorkflowScheduleQueryOptions({ workflowId, scheduleId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Workflow Schedule
 */
export const invalidateDeleteWorkflowSchedule = async (
  queryClient: QueryClient,
  { workflowId, scheduleId }: DeleteWorkflowSchedulePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteWorkflowScheduleQueryKey({ workflowId, scheduleId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Workflow Schedule
 */
export const updateWorkflowSchedule = (
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowScheduleResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/schedules/${scheduleId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLWorkflowScheduleUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateWorkflowScheduleQueryKey = (
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest?: BodyType<TLWorkflowScheduleUpdateRequest>,
) => {
  return [
    'PATCH',
    'api',
    'v2',
    'workflows',
    workflowId,
    'schedules',
    scheduleId,
    tLWorkflowScheduleUpdateRequest,
  ] as const;
};

export const getUpdateWorkflowScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateWorkflowScheduleQueryKey({ workflowId, scheduleId }, tLWorkflowScheduleUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateWorkflowSchedule>>> = ({ signal }) =>
    updateWorkflowSchedule({ workflowId, scheduleId }, tLWorkflowScheduleUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateWorkflowScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof updateWorkflowSchedule>>>;
export type UpdateWorkflowScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof updateWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateWorkflowSchedule>>,
          TError,
          Awaited<ReturnType<typeof updateWorkflowSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Workflow Schedule
 */

export function useUpdateWorkflowSchedule<
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateWorkflowScheduleQueryOptions(
    { workflowId, scheduleId },
    tLWorkflowScheduleUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Workflow Schedule
 */
export const prefetchUpdateWorkflowScheduleQuery = async <
  TData = Awaited<ReturnType<typeof updateWorkflowSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateWorkflowSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateWorkflowScheduleQueryOptions(
    { workflowId, scheduleId },
    tLWorkflowScheduleUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Workflow Schedule
 */
export const invalidateUpdateWorkflowSchedule = async (
  queryClient: QueryClient,
  { workflowId, scheduleId }: UpdateWorkflowSchedulePathParameters,
  tLWorkflowScheduleUpdateRequest: BodyType<TLWorkflowScheduleUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateWorkflowScheduleQueryKey({ workflowId, scheduleId }, tLWorkflowScheduleUpdateRequest) },
    options,
  );

  return queryClient;
};
